import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ParentSize } from '@visx/responsive';
import { useTheme } from '@material-ui/core';
import { FDSecondaryActionButton, FDStack } from '@facet-wealth/design';
import useUserContext from 'Context/UserContext';
import useMobileLayout from 'Hooks/useMobileLayout';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import Typography from 'Components/UI/Typography';
import BarChart from 'Components/UI/BarChart';
import FullLayout from 'Components/Layout/Full';
import DefaultOrFDContentContainer from 'Components/Layout/DefaultOrFDContentContainer';
import useHomeContext from 'Context/HomeContext';
import { useHistory } from 'react-router';
import { balanceSheetPageURL } from 'Hooks/useBalanceSheetPageURL';
import AccountList from './AccountList';

const styles = theme => ({
  root: {
    padding: theme.spacing(4, 2),
  },
  rootWhiteBackground: {
    backgroundColor: theme.palette.common.white,
  },
  chartContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  chartAccountContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  chartContainerMobile: {
    flexDirection: 'column',
    alignItems: 'center',
  },
  barChart: {
    margin: theme.spacing(4, 0),
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.shortest,
    }),
  },
  title: {},
  titleHidden: {
    display: 'none',
  },
});

const balanceSheetSctions = {
  assets: {
    title: 'Assets',
    color: 'completeGreen',
  },
  liabilities: {
    title: 'Liabilities',
    color: 'reviewPurple',
  },
};

const BalanceSheetChart = ({
  data,
  classes,
  isMobile,
  AccountListProps,
  className,
}) => {
  const { isAgent } = useUserContext();
  const theme = useTheme();

  return (
    <ParentSize className={className}>
      {parent => {
        // eslint-disable-next-line
        const maxAccounts = Object.values(data).reduce((acc, curr) => acc.items.length > curr.items.length
          ? acc.items.length
          : curr.items.length);

        const chartWidth = isMobile
          ? parent.width - theme.spacing(2) // There's padding on either side that needs to be accounted for like this
          : parent.width / 2 - theme.spacing(6);
        return Object.keys(data).map((section) => {
          const sectionData = data[section];
          const accountDiff = maxAccounts - sectionData.items.length;
          const sectionBarStyle = {
            paddingBottom: accountDiff * 29, // <-- Difference in num of accounts * 29 (height of each barChart item)
          };
          return (
            <div className={classes.chartAccountContainer} key={section}>
              <BarChart
                style={sectionBarStyle}
                className={classes.barChart}
                width={chartWidth}
                title={balanceSheetSctions[section].title}
                color={balanceSheetSctions[section].color}
                items={sectionData.items}
              />
              <AccountList
                color={balanceSheetSctions[section].color}
                items={sectionData.items}
                totalBalance={sectionData.totalBalance}
                width={chartWidth}
                readOnly={isAgent}
                {...AccountListProps}
              />
            </div>
          );
        });
      }}
    </ParentSize>
  );
};

const BalanceSheet = ({
  className,
  classes,
  data,
  showTitle,
  householdResource,
  BalanceSheetChartProps,
  BalanceSheetTitleProps,
}) => {
  const isMobile = useMobileLayout();
  const history = useHistory();
  const { hasLegacyDashboardImprovement } = useHomeContext();

  if (!data) {
    return null;
  }

  return (
    <FullLayout
      className={classNames(classes.root, className, {
        [classes.rootWhiteBackground]: !hasLegacyDashboardImprovement,
      })}
      test={'balance-sheet-widget'}
    >
      <DefaultOrFDContentContainer>
        <Typography
          className={classNames(classes.title, {
            [classes.titleHidden]: !showTitle,
          })}
          variant="h4"
          color="black"
          weight="bold"
          {...BalanceSheetTitleProps}
        >
          Balance Sheet
        </Typography>
        <BalanceSheetChart
          className={classNames(classes.chartContainer, {
            [classes.chartContainerMobile]: isMobile,
          })}
          data={data}
          classes={classes}
          debounceTime={100}
          ignoreDimensions={['height', 'top', 'left']}
          isMobile={isMobile}
          {...BalanceSheetChartProps}
        />
      </DefaultOrFDContentContainer>
      <Box paddingTop={2}>
        <FDStack justifyContent="center">
          <FDSecondaryActionButton
            onClick={() => history.push(balanceSheetPageURL(householdResource, householdResource.id()))}
            label="View Balance Sheet"
            size="sm"
          />
        </FDStack>
      </Box>
    </FullLayout>
  );
};

BalanceSheetChart.defaultProps = {
  classes: {},
  isMobile: false,
  AccountListProps: {},
  className: '',
};

BalanceSheetChart.propTypes = {
  data: PropTypes.object.isRequired,
  classes: PropTypes.object,
  isMobile: PropTypes.bool,
  AccountListProps: PropTypes.object,
  className: PropTypes.string,
};

BalanceSheet.defaultProps = {
  className: '',
  showTitle: true,
  householdResource: undefined,
};

BalanceSheet.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  showTitle: PropTypes.bool,
  householdResource: PropTypes.object,
};

export default withStyles(styles)(BalanceSheet);
